import { Avatar } from '@/deploy/components/ui/Avatar';
import { EnvBadge } from '@/deploy/components/ui/EnvBadge';
import { Badge } from '@/deploy/components/ui/Badge';
import { Icon } from '@/deploy/components/ui/Icon';
import type { Deployment } from '@/deploy/types';
import { formatDateTime, formatDuration, formatRelative } from '@/deploy/utils/format';
import styles from './DeploymentItem.module.scss';

interface DeploymentItemProps {
  deployment: Deployment;
  compact?: boolean;
}

export function DeploymentItem({ deployment, compact }: DeploymentItemProps) {
  const statusBadge = (() => {
    switch (deployment.status) {
      case 'success':   return <Badge tone="success" dot>Succès</Badge>;
      case 'running':   return <Badge tone="info" dot>En cours</Badge>;
      case 'queued':    return <Badge tone="muted" dot>En attente</Badge>;
      case 'failed':    return <Badge tone="danger" dot>Échec</Badge>;
      case 'cancelled': return <Badge tone="muted">Annulé</Badge>;
      default:          return <Badge tone="muted">Idle</Badge>;
    }
  })();

  return (
    <div className={[styles.item, compact && styles.compact].filter(Boolean).join(' ')}>
      <div className={styles.left}>
        <Avatar name={deployment.author.name} color={deployment.author.avatarColor} size={36} />
        <div className={styles.details}>
          <div className={styles.title}>
            <span>{deployment.author.name}</span>
            <span className={styles.muted}>a déployé</span>
            <EnvBadge env={deployment.source} size="sm" />
            <Icon name="arrow-right" size={12} />
            <EnvBadge env={deployment.target} size="sm" />
          </div>
          <div className={styles.meta}>
            <span title={formatDateTime(deployment.startedAt)}>{formatRelative(deployment.startedAt)}</span>
            <span className={styles.dot} />
            <span>{deployment.items.length} {deployment.kind}{deployment.items.length > 1 ? 's' : ''}</span>
            {deployment.durationSec !== undefined && (
              <>
                <span className={styles.dot} />
                <span>{formatDuration(deployment.durationSec)}</span>
              </>
            )}
            {deployment.restartAfter && (
              <>
                <span className={styles.dot} />
                <span className={styles.restart}><Icon name="refresh" size={10} /> restart</span>
              </>
            )}
          </div>
          {deployment.note && <div className={styles.note}>{deployment.note}</div>}
        </div>
      </div>
      {statusBadge}
    </div>
  );
}
